import { Fragment, useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import { useSelector } from 'statux'

import DashboardEbpcApplicationReviewerForm
    from './DashboardEbpcApplicationReviewerForm/DashboardEbpcApplicationReviewerForm'
import DashboardEbpcApplicationReviewRate
    from './DashboardEbpcApplicationReviewRate/DashboardEbpcApplicationReviewRate'
import DashboardEbpcAssignmentsTable
    from '../DashboardEbpcAssignments/DashboardEbpcAssignmentsTable/DashboardEbpcAssignmentsTable'

import Alert from '../../../components/Alert/Alert'
import Badge from '../../../components/Badge/Badge'
import Details from '../../../components/Details/Details'
import Image from '../../../components/Image/Image'
import RichText from '../../../components/RichText/RichText'

import { camelCase, getInitials } from '../../../utils/strings'
import { getCssVar } from '../../../utils/css'
import { graphqlClient, gql } from '../../../utils/gql'

import authProvider from '../../../utils/auth-provider'

export default function DashboardEbpcApplicationReview (props = {
    steps: null
}) {
    const role = authProvider.getDefaultRole()

    const isMember = role === 'member'

    const isEbpcJudge = role === 'ebpc_judge'

    const isEbpcReviewer = role === 'ebpc_reviewer'

    const isEbpcRole = isEbpcJudge
        || isEbpcReviewer

    const isAdmin = role === 'admin'

    const history = useHistory()

    const params = useParams()

    const {
        ebpcContest,
        ebpcAssignmentsEndDateStr,
        ebpcAssignmentsOpen,
        ebpcApplicationsOpen,
        ebpcReviewsOpen,
        ebpcFinalsOpen,
        ebpcFinalsEndDateStr
    } = useSelector()

    const [{
        application,
        assignments,
        assignment,
        refreshAssignments
    }, setState] = useState({
        application: (history.state || {}).application || null,
        assignments: (history.state || {}).assignments || null,
        assignment: (history.state || {}).assignment || null,
        refreshAssignments: false
    })

    useEffect(async () => {
        let redirect = '/dashboard'

        if (isMember
                && ebpcApplicationsOpen)
            redirect += '/ebpc/application'

        const ebpcContestId = params.contestId
            || (ebpcContest || {}).id

        if (!params.userId
                || !ebpcContestId
                || (!isAdmin
                    && !isEbpcRole))
            return history.push(redirect)

        const queryJudgeAssignments = isEbpcJudge
            ? gql`
                ebpc_assignments (
                    where: {
                        ebpc_judge_id: {
                            _eq: "${authProvider.getIdentity().id}"
                        },
                        ebpc_application: {
                            ebpc_contest_id: {
                                _eq: ${(ebpcContest || {}).id}
                            },
                            submitted_at: {
                                _is_null: false
                            }
                        },
                        ebpc_finals: {
                            _eq: ${ebpcFinalsOpen}
                        }
                    },
                    order_by: {
                        ebpc_application_id: asc
                    }
                ) {
                    feedback
                    feedback_admin
                    invest
                    ranking
                    rating
                    submitted_at
                    recused_at
                    ebpc_finals
                    ebpc_ratings (where: {
                        ${ebpcFinalsOpen
                            ? gql`ebpc_finals: { _eq: true }`
                            : ''}
                    }) {
                        id
                        type
                        rating
                    }
                    ebpc_application {
                        id
                        data
                        venture: data(path: "aboutYourVenture.ventureName")
                        profile {
                            company
                            user {
                                id
                            }
                        }
                    }
                }

                ebpc_ratings_types {
                    slug
                    title
                }

                ebpc_ratings_types_aggregate {
                    aggregate {
                        count
                    }
                }
            `
            : ''

        const query = await graphqlClient
            .request(gql`
                query {
                    ebpc_applications (
                        limit: 1,
                        order_by: { submitted_at: desc },
                        where: {
                            profile: { user: { id: { _eq: "${params.userId}" } } },
                            ebpc_contest_id: { _eq: ${ebpcContestId} },
                        }
                    ) {
                        id
                        data
                        venture: data(path: "aboutYourVenture.ventureName")
                        created_at
                        submitted_at
                        profile {
                            company
                            user {
                                id
                                avatar_url
                                display_name
                            }
                        }
                        ebpc_review {
                            id
                            ebpc_reviewer_id
                            approved
                            created_at
                            updated_at
                            ${isEbpcReviewer
                                ? gql`
                                    ebpc_application {
                                        ebpc_review_approved
                                    }`
                                : ''}
                        }
                    }

                    ${queryJudgeAssignments}
                }
            `, {}, {
                headers: {
                    'x-hasura-role': isEbpcReviewer
                        ? 'ebpc_reviewer'
                        : 'user'
                }
            })
            .catch((err) => {
                console.warn(err)
                return {}
            })

        const queryApplication = ((query || {}).ebpc_applications || [])[0]
            || false

        if (queryApplication)
            queryApplication.data = queryApplication.data || {}

        if (queryApplication
                && !queryApplication.venture)
            queryApplication.venture
                = queryApplication.profile.company

        let planPrefix = ''

        if ((queryApplication || {}).created_at) {
            const createdAt = new Date(queryApplication.created_at)

            planPrefix = `${String(createdAt.getFullYear()).slice(2)}-`
        }

        if (queryApplication
                && !queryApplication.plan)
            queryApplication.plan = `${planPrefix}${`${queryApplication.id}`
                    .padStart(4, '0')}`

        if (isAdmin
                && queryApplication
                && !queryApplication.status) {
            queryApplication.status = queryApplication.submitted_at
                ? 'Complete'
                : 'In progress'

            queryApplication.statusType = queryApplication.submitted_at
                ? 'success'
                : 'warning'
        }

        const queryAssignments = ((query || {}).ebpc_assignments || [])
            .map((obj) => {
                const ratingsTypesCount = query.ebpc_ratings_types_aggregate
                    .aggregate
                    .count || 0

                if (!ebpcFinalsOpen) {
                    obj.status = 'Not started'
                    obj.statusType = null

                    if (obj.ebpc_ratings.length
                            || obj.rating
                            || obj.invest !== null) {
                        obj.status = 'In progress'
                        obj.statusType = 'warning'
                    }

                    if (ratingsTypesCount === obj.ebpc_ratings.length
                            && obj.rating
                            && obj.invest !== null) {
                        obj.status = 'Completed'
                        obj.statusType = 'success'
                    }

                    if (obj.recused_at) {
                        obj.status = 'Recused'
                        obj.statusType = 'danger'
                    }
                }

                obj.plan = `24-${`${obj.ebpc_application.id}`
                    .padStart(4, '0')}`

                obj.venture = obj.ebpc_application.venture
                    || obj.ebpc_application.profile.company

                obj.applicant = obj.ebpc_application.profile.user

                obj.ratingsTypes = query.ebpc_ratings_types

                return obj
            })

        if (JSON.stringify(queryApplication)
                    === JSON.stringify(application)
                && JSON.stringify(queryAssignments)
                    === JSON.stringify(assignments))
            return

        setState({
            application: queryApplication,
            assignments: queryAssignments,
            refreshAssignments: false
        })

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                application: queryApplication,
                assignments: queryAssignments
            }))
    }, [])

    useEffect(async () => {
        let redirect = '/dashboard'

        if (isMember)
            redirect += '/ebpc/application'

        if ((!application
                    && application !== null)
                || (!isAdmin
                        && application !== null
                        && !(application || {}).submitted_at))
            return history.push(redirect)
    }, [application])

    useEffect(() => {
        if (!(assignments || []).length)
            return assignment
                && setState({
                    assignment: false,
                    assignments,
                    application
                })

        const activeAssignment = assignments
            .filter(obj => (obj.applicant || {}).id === params.userId)[0]

        if (JSON.stringify(activeAssignment)
                === JSON.stringify(assignment))
            return

        setState({
            refreshAssignments: false,
            assignment: activeAssignment,
            assignments,
            application
        })
    }, [assignments, params])

    if ((!(application || {}).data
                && application !== null)
            || (!isAdmin
                && application
                && !(application || {}).submitted_at))
        return null

    const onUpdateAssignment = ({
        ebpc_ratings, // eslint-disable-line
        ...rest
    }) => {
        let isRefreshAssignments = rest.rating !== assignment.rating

        const updateAssignment = Object.assign(
            JSON.parse(JSON.stringify(assignment)),
            rest)

        updateAssignment.ebpc_ratings = Object.keys(ebpc_ratings)
            .map((key) => {
                const rating = (updateAssignment.ebpc_ratings
                    .filter(obj => obj.type === key)[0]
                        || { type: key })

                rating.rating = ebpc_ratings[key]

                return rating
            })

        if (updateAssignment.statusType !== null
                && updateAssignment.rating
                && updateAssignment.invest !== null
                && !updateAssignment.ebpc_ratings
                    .filter(obj => !obj.rating)
                    .length) {
            isRefreshAssignments = true

            updateAssignment.status = 'Completed'
            updateAssignment.statusType = 'success'
        }

        const updateAssignments = JSON.parse(JSON.stringify(assignments))
            .map(obj => obj.plan === updateAssignment.plan
                ? updateAssignment
                : obj)

        setState({
            assignment: updateAssignment,
            assignments: updateAssignments,
            refreshAssignments: isRefreshAssignments
                ? (new Date()).getTime()
                : false,
            application
        })
    }

    const onRecusal = async (e) => {
        e.target.disabled = true

        const mutation = await graphqlClient
            .request(gql`
                mutation {
                    delete_ebpc_ratings (
                        where: {
                            ebpc_judge_id: {
                                _eq: "${authProvider.getIdentity().id}"
                            },
                            ebpc_application_id: {
                                _eq: ${application.id}
                            }
                        }
                    ) {
                        affected_rows
                    }

                    update_ebpc_assignments (
                        where: {
                            ebpc_judge_id: {
                                _eq: "${authProvider.getIdentity().id}"
                            },
                            ebpc_application_id: {
                                _eq: ${application.id}
                            }
                        },
                        _set: {
                            feedback: null,
                            feedback_share: false,
                            invest: null,
                            ranking: null,
                            rating: null,
                            recused_at: "${(new Date()).toISOString()}"
                        }
                    ) {
                        returning {
                            feedback
                            feedback_share
                            invest
                            ranking
                            rating
                            submitted_at
                            recused_at
                            ebpc_ratings {
                                id
                                type
                                rating
                            }
                        }
                    }
                }
            `)
            .catch((err) => {
                console.warn(err)
                return {}
            })

        const updateAssignment = Object.assign(
            JSON.parse(JSON.stringify(assignment)),
            ((mutation || {}).update_ebpc_assignments || [])[0] || {})

        updateAssignment.status = 'Recused'
        updateAssignment.statusType = 'danger'
        updateAssignment.ebpc_ratings = []

        const updateAssignments = assignments
            .map(obj => obj.ebpc_application.id === application.id
                ? updateAssignment
                : obj)

        setState({
            assignment: updateAssignment,
            assignments: updateAssignments,
            refreshAssignments: true,
            application
        })
    }

    let { data } = application || {}

    data = data || {}

    data.contactInformation = data.contactInformation || {}

    data.aboutYourFounder = data.aboutYourFounder || {}

    data.aboutYourVenture = data.aboutYourVenture || {}

    data.materials = data.materials || {}

    const isContactInfoName = data.contactInformation.givenName
        && data.contactInformation.familyName

    const displayName = (isContactInfoName
        ? `${data.contactInformation.givenName} ${
            data.contactInformation.familyName}`
        : (((application || {}).profile || {}).user || {}).display_name)
            || ''

    const fullName = isContactInfoName
        ? `${data.contactInformation.honorificSuffix || ''} ${displayName}`
            .trim()
        : displayName

    let geolocation = data.contactInformation.addressLevel2 || ''

    if (data.contactInformation.addressLevel1)
        geolocation += `${geolocation
            ? ', '
            : ''}${data.contactInformation.addressLevel1}`

    if (data.contactInformation.country)
        geolocation += `${geolocation
            ? ', '
            : ''}${data.contactInformation.country}`

    let cofounders = `${data.aboutYourVenture.coFounder1 || ''}${
        data.aboutYourVenture.coFounder2
            ? `, ${data.aboutYourVenture.coFounder2}`
            : ''}${
        data.aboutYourVenture.coFounder3
            ? `, ${data.aboutYourVenture.coFounder3}`
            : ''}`

    cofounders = cofounders
        .trim()
        .replace(/(^,|,$)/g, '')

    const status = (assignment || {}).status
        || (application || {}).status

    const statusType = (assignment || {}).statusType
        || (application || {}).statusType

    const isRecused = Boolean((assignment || {}).recused_at)

    const reviewSteps = (isAdmin
            || isEbpcReviewer
            || (isEbpcJudge
                && (!Object.keys(assignment || {}).length
                    || isRecused
                    || !ebpcAssignmentsOpen)))
        && (props.steps || [])
            .filter(obj => (data[camelCase(obj.slug)]
                || {})[`${camelCase(obj.slug)}Summary`])
            .map((obj) => {
                obj.answer = data[camelCase(obj.slug)][`${camelCase(obj.slug)}Summary`]

                obj.richText = (obj.richText || '')
                    .replace(/(.)\*(<\/)/, '$1$2')

                if (obj.slug === 'finances')
                    obj.upload = data.finances.financesFile

                return obj
            })

    const isAssignmentsSubmitted = isEbpcJudge
        && (assignments || []).length
        && assignments.length
            === assignments
                .filter(obj => obj.submitted_at)
                .length

    const activeFinalsAssignmentIdx = ebpcFinalsOpen
        && (assignments || [])
            .map((obj, idx) => (obj.applicant || {}).id === params.userId
                ? idx
                : null)
            .filter(int => int !== null)[0]

    const nextFinalsAssignment = typeof activeFinalsAssignmentIdx === 'number'
        && activeFinalsAssignmentIdx >= 0
        && assignments[activeFinalsAssignmentIdx + 1]

    const nextFinalsLink = nextFinalsAssignment
        && `/dashboard/ebpc/applications/${nextFinalsAssignment.applicant.id}`

    return (
        <div
            className="dashboard-ebpc-application-review pad-3-t pad-4-b pad-4-t-lg-up"
        >
            <div className="container">
                <div className="row-condensed flex-align-items-center flex-justify-content-space-between margin-3-b">
                    <div className="col-auto display-flex flex-align-items-center">
                        <Link
                            to="/dashboard"
                            className="body-4 display-inline-flex"
                            style={{
                                color: getCssVar('--slate'),
                                overflow: 'hidden'
                            }}
                        >
                            <svg
                                className="icon-chevron-left flex-none"
                                width="18"
                                height="18"
                                style={{ margin: '-2px 2px 0 -5px' }}
                            >
                                <use xlinkHref="#icon-chevron-left" />
                            </svg>
                            <span>
                                <span className="display-none-sm-down">Go back </span>
                                <span className="text-transform-capitalize display-none-sm-up">back </span>
                                to {isEbpcJudge
                                    ? 'EBPC judging portal'
                                    : 'Dashboard'}
                            </span>
                        </Link>
                    </div>
                    {isEbpcJudge
                        && ebpcAssignmentsOpen
                        && (application === null
                            || !isAssignmentsSubmitted)
                        && (
                            <div
                                className="col-auto col-12-md-down margin-auto margin-0-r"
                                style={{
                                    visibility: application === null
                                        ? 'hidden'
                                        : null
                                }}
                            >
                                <div className="row flex-align-items-center">
                                    <div className="col-auto pad-0-r margin-1-t margin-0-t-md-up">
                                        <p
                                            className="body-4"
                                            style={{
                                                color: getCssVar('--slate')
                                            }}
                                        >
                                            Finished reviewing all ventures?
                                        </p>
                                    </div>
                                    <div className="col-auto margin-1-t margin-0-t-md-up">
                                        <Link
                                            to="/dashboard/ebpc/assignments"
                                            className="btn btn-secondary"
                                        >
                                            Finalize
                                        </Link>
                                    </div>
                                </div>
                            </div>)}
                </div>

                <div className="row-condensed">
                    <div className="col">
                        <div className="card card-offscreen-x-lg-down">
                            <section className="dashboard-ebpc-application-review-header">
                                <div className="row-condensed flex-justify-content-space-between">
                                    <div className="col-12">
                                        <div className="row-condensed flex-justify-content-space-between">
                                            <div className="col-12 col-7-lg-up">
                                                <h1 className="heading-2 display-flex flex-direction-column">
                                                    <span className="row-condensed margin-1-b">
                                                        <span className="heading-5 display-inline-flex col-auto">
                                                            Plan #{(application || '').plan || ''}
                                                        </span>
                                                        {status
                                                            && (
                                                                <span className="display-inline-flex col-auto pad-0-l">
                                                                    <Badge
                                                                        type={statusType}
                                                                    >
                                                                        {status}
                                                                    </Badge>
                                                                </span>)}
                                                    </span>
                                                    <span
                                                        style={{ whiteSpace: 'pre' }}
                                                    >
                                                        {(application || {}).venture || ' '}
                                                    </span>
                                                </h1>
                                            </div>
                                            {isEbpcJudge
                                                && assignment
                                                && ebpcAssignmentsOpen
                                                && !isRecused
                                                && !isAssignmentsSubmitted
                                                && (
                                                    <div className="col-auto col-12-lg-down margin-1-t-lg-down">
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            style={{
                                                                borderColor: getCssVar('--cadet'),
                                                                fontSize: 13
                                                            }}
                                                            onClick={onRecusal}
                                                        >
                                                            Recuse from judging this venture
                                                        </button>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                    {isEbpcJudge
                                        && ebpcAssignmentsEndDateStr
                                        && ebpcAssignmentsOpen
                                        && assignment
                                        && !isRecused
                                        && !isAssignmentsSubmitted
                                        && (
                                            <div className="col-12 rich-text body-4 margin-1-t">
                                                <p>Please complete your review by {ebpcAssignmentsEndDateStr}.</p>
                                            </div>
                                        )}
                                    {isEbpcJudge
                                        && ebpcFinalsEndDateStr
                                        && ebpcFinalsOpen
                                        && assignment
                                        && !isAssignmentsSubmitted
                                        && (
                                            <div className="col-12 rich-text body-4 margin-1-t">
                                                <p>Please complete your review by {ebpcFinalsEndDateStr}.</p>
                                            </div>
                                        )}
                                    <div className="col-12 rich-text body-3 margin-t">
                                        <small
                                            className="display-block"
                                            role="status"
                                            style={{
                                                borderLeft: '2px solid #28FBAC',
                                                paddingLeft: 15,
                                                lineHeight: 1.4
                                            }}
                                        >
                                            All information displayed below was submitted by the founder or a
                                            representative of the venture on their behalf. Catapult asks a multitude
                                            of questions and ventures are free to respond how they see fit. We do not
                                            write or generate responses on their behalf.
                                        </small>
                                    </div>
                                </div>
                                {(assignment || {}).statusType === 'warning'
                                    && (assignment.ratingsTypes || []).length
                                    && (
                                        <Alert
                                            className="margin-t"
                                            dismissible={false}
                                            iconClassName="margin-0-t"
                                            style={{
                                                borderRadius: 5,
                                                backgroundColor: getCssVar('--glaze')
                                            }}
                                            icon
                                            fluidCondensed
                                        >
                                            <div
                                                className="body-5"
                                                style={{
                                                    paddingTop: 1.5
                                                }}
                                            >
                                                <p>
                                                    You haven't completed {
                                                        assignment.ratingsTypes
                                                            .map(({ slug, title }) => ({
                                                                rating: (assignment.ebpc_ratings
                                                                    .filter(obj => obj.type === slug)[0] || {})
                                                                    .rating || null,
                                                                slug,
                                                                title
                                                            }))
                                                            .concat([{
                                                                slug: 'rating',
                                                                title: 'Overall',
                                                                rating: assignment.rating
                                                            }, {
                                                                slug: 'invest',
                                                                title: 'Investment',
                                                                rating: assignment.invest
                                                            }])
                                                            .filter(obj => !obj.rating)
                                                            .map((obj, idx, arr) => (
                                                                <Fragment
                                                                    key={`alert-warning-ratings-${idx}`}
                                                                >
                                                                    {arr.length > 1
                                                                        && Boolean(idx)
                                                                        && (
                                                                            idx + 1 === arr.length
                                                                                ? <span>, and </span>
                                                                                : <span>, </span>)}
                                                                    <a
                                                                        href={`#${obj.slug}`}
                                                                    >
                                                                        {obj.title}
                                                                    </a>
                                                                </Fragment>
                                                            ))
                                                    }.
                                                </p>
                                            </div>
                                        </Alert>
                                    )}
                                {isEbpcJudge
                                    && assignment
                                    && ebpcAssignmentsOpen
                                    && !isRecused
                                    && !isAssignmentsSubmitted
                                    && (
                                        <div className="margin-t display-none-xl-up">
                                            <a
                                                href="#dashboard-ebpc-application-review-rankings"
                                                className="body-5 text-decoration-underline"
                                            >
                                                Jump to rank ventures
                                            </a>
                                        </div>
                                    )}
                            </section>

                            <section className="dashboard-ebpc-application-review-applicant margin-4-t margin-4-b">
                                <h2 className="heading-5 margin-b">Applicant information</h2>
                                <div
                                    className="card card-border pad-0"
                                    style={{
                                        borderRadius: 5,
                                        boxShadow: 'none',
                                        color: getCssVar('--navy'),
                                        fontSize: 14,
                                        overflow: 'visible'
                                    }}
                                >
                                    <div className="row-condensed">
                                        <div
                                            className={`col-12 display-flex${
                                                data.aboutYourFounder.about
                                                        || data.aboutYourFounder.linkedin
                                                    ? ' col-4-lg-up col-5-xl'
                                                    : ''}`}
                                        >
                                            <div
                                                className="dashboard-ebpc-application-review-applicant-contact flex-grow"
                                                style={{
                                                    backgroundColor: '#BCFFE6'
                                                }}
                                            >
                                                <div className="display-flex">
                                                    <Link
                                                        className="margin-1-r flex-none"
                                                        to={`/dashboard/profiles/${(((application || {}).profile || {}).user || {}).id}`}
                                                        title={`View profile of ${displayName}`}
                                                        tabIndex="-1"
                                                        style={{
                                                            marginBottom: 'auto'
                                                        }}
                                                    >
                                                        <span
                                                            className="avatar avatar-sm"
                                                            aria-hidden="true"
                                                        >
                                                            <Image
                                                                src={(((application || {}).profile || {}).user || {}).avatar_url}
                                                                textFallback={getInitials(displayName || ' ')}
                                                                data-object-fit="cover"
                                                            />
                                                        </span>
                                                    </Link>
                                                    <div className="display-flex flex-direction-column">
                                                        <address
                                                            role={!geolocation
                                                                    && application !== null
                                                                ? 'none'
                                                                : null}
                                                            style={{
                                                                whiteSpace: application === null
                                                                    ? 'pre'
                                                                    : null
                                                            }}
                                                        >
                                                            <Link
                                                                className="font-weight-normal"
                                                                to={`/dashboard/profiles/${(((application || {}).profile || {}).user || {}).id}`}
                                                                title={`View profile of ${displayName}`}
                                                            >
                                                                {fullName || ' '}
                                                            </Link>
                                                            {(data.contactInformation.organizationTitle
                                                                    || application === null)
                                                                && (
                                                                    <span
                                                                        className="display-block body-5"
                                                                    >
                                                                        {data.contactInformation.organizationTitle || ' '}
                                                                    </span>)}
                                                            {data.contactInformation.organization
                                                                && (
                                                                    <span className="display-block body-5">
                                                                        {data.contactInformation.organization || ' '}
                                                                    </span>)}
                                                            {geolocation
                                                                && (
                                                                    <span className="display-block body-5">
                                                                        {geolocation || ' '}
                                                                    </span>)}
                                                        </address>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {Boolean(application === null
                                                || data.aboutYourFounder.about
                                                || data.aboutYourFounder.linkedin)
                                            && (
                                                <div className="col">
                                                    <div className="pad pad-0-l-lg-up pad-3-r-lg-up">
                                                        <RichText
                                                            style={{ color: getCssVar('--navy') }}
                                                        >
                                                            {data.aboutYourFounder.about
                                                                && <p>{data.aboutYourFounder.about}</p>}
                                                            {data.aboutYourFounder.linkedin
                                                                && (
                                                                    <p>
                                                                        <a
                                                                            className="body-5"
                                                                            href={data.aboutYourFounder.linkedin}
                                                                            rel="noopener noreferrer"
                                                                            target="_blank"
                                                                            title="Opens in a new tab"
                                                                        >
                                                                            {data.aboutYourFounder.linkedin
                                                                                .replace(/^https*:\/\//, '')}
                                                                        </a>
                                                                    </p>)}
                                                        </RichText>
                                                    </div>
                                                </div>)}
                                    </div>
                                </div>
                                <h3 className="heading-6 margin-t font-weight-medium">
                                    Barriers overcome on entrepreneurial journey identified by applicant
                                </h3>
                                <RichText
                                    className="body-3 margin-1-t"
                                    html={(data.aboutYourFounder.barriers || `${
                                            application !== null
                                                ? 'N/A'
                                                : '&nbsp;'}`)
                                        .split(/\n+/g)
                                        .map(str => str.trim())
                                        .filter(str => str)
                                        .map(str => `<p>${str}</p>`)
                                        .join('')}
                                    style={{
                                        color: getCssVar('--oxford')
                                    }}
                                />
                                <Details className="card-border margin-t">
                                    <summary>
                                        <span className="body-4 font-weight-medium">
                                            More about this founder
                                        </span>
                                    </summary>
                                    <div className="body-4 pad-3-r">
                                        <hr
                                            className="margin-0-t"
                                            style={{
                                                borderColor: getCssVar('--geyser')
                                            }}
                                        />
                                        <ul>
                                            <li className="row-condensed margin-b margin-1-b-sm-up">
                                                <span
                                                    className="col-12 col-4-sm-up"
                                                    style={{
                                                        color: getCssVar('--navy')
                                                    }}
                                                >
                                                    Gender
                                                </span>
                                                <span className="col">
                                                    {(data.aboutYourFounder.gender
                                                            || [application !== null
                                                                ? 'N/A'
                                                                : null])
                                                        .filter(str => str)
                                                        .map(str => data.aboutYourFounder[camelCase(`gender ${str}`)]
                                                            ? `${str}: ${data.aboutYourFounder[camelCase(`gender ${str}`)]}`
                                                            : str)
                                                        .join(', ')}
                                                </span>
                                            </li>
                                            <li className="row-condensed margin-b margin-1-b-sm-up">
                                                <span
                                                    className="col-12 col-4-sm-up"
                                                    style={{
                                                        color: getCssVar('--navy')
                                                    }}
                                                >
                                                    Racial/ethnic background
                                                </span>
                                                <span className="col">
                                                    {(data.aboutYourFounder.ethnicity
                                                            || [application !== null
                                                                ? 'N/A'
                                                                : null])
                                                        .filter(str => str)
                                                        .map(str => data.aboutYourFounder[camelCase(`ethnicity ${str}`)]
                                                            ? `${str}: ${data.aboutYourFounder[camelCase(`ethnicity ${str}`)]}`
                                                            : str)
                                                        .join(', ')}
                                                </span>
                                            </li>
                                            <li className="row-condensed margin-b margin-1-b-sm-up">
                                                <span
                                                    className="col-12 col-4-sm-up"
                                                    style={{
                                                        color: getCssVar('--navy')
                                                    }}
                                                >
                                                    Person of color
                                                </span>
                                                <span className="col">
                                                    {data.aboutYourFounder.personOfColor
                                                        || `${application !== null
                                                            ? 'N/A'
                                                            : ''}`}
                                                </span>
                                            </li>
                                            <li className="row-condensed margin-b margin-1-b-sm-up">
                                                <span
                                                    className="col-12 col-4-sm-up"
                                                    style={{
                                                        color: getCssVar('--navy')
                                                    }}
                                                >
                                                    LGBTQIA+
                                                </span>
                                                <span className="col">
                                                    {data.aboutYourFounder.lgbtqia
                                                        || `${application !== null
                                                            ? 'N/A'
                                                            : ''}`}
                                                </span>
                                            </li>
                                            <li className="row-condensed margin-b margin-1-b-sm-up">
                                                <span
                                                    className="col-12 col-4-sm-up"
                                                    style={{
                                                        color: getCssVar('--navy')
                                                    }}
                                                >
                                                    Current age
                                                </span>
                                                <span className="col">
                                                    {data.aboutYourFounder.age
                                                        || `${application !== null
                                                            ? 'N/A'
                                                            : ''}`}
                                                </span>
                                            </li>
                                            <li className="row-condensed margin-b margin-1-b-sm-up">
                                                <span
                                                    className="col-12 col-4-sm-up"
                                                    style={{
                                                        color: getCssVar('--navy')
                                                    }}
                                                >
                                                    Highest degree obtained
                                                </span>
                                                <span className="col">
                                                    {(data.aboutYourFounder.educationOther
                                                        ? `Other: ${data.aboutYourFounder.educationOther}`
                                                        : data.aboutYourFounder.education)
                                                            || `${application !== null
                                                                ? 'N/A'
                                                                : ''}`}
                                                </span>
                                            </li>
                                            <li className="row-condensed">
                                                <span
                                                    className="col-12 col-4-sm-up"
                                                    style={{
                                                        color: getCssVar('--navy')
                                                    }}
                                                >
                                                    Current employment
                                                </span>
                                                <span className="col">
                                                    {(data.aboutYourFounder.work
                                                            || [application !== null
                                                                ? 'N/A'
                                                                : null])
                                                        .filter(str => str)
                                                        .map(str => data.aboutYourFounder[camelCase(`work ${str}`)]
                                                            ? `${str}: ${data.aboutYourFounder[camelCase(`work ${str}`)]}`
                                                            : str)
                                                        .join(', ')}
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                </Details>
                            </section>

                            <section className="dashboard-ebpc-application-review-venture margin-4-t margin-4-b">
                                <h2 className="heading-5 margin-b">Venture information</h2>
                                {Boolean((data.aboutYourVenture.ventureAbout || '').trim())
                                    && (
                                        <RichText
                                            className="body-3 margin-b"
                                            html={data.aboutYourVenture.ventureAbout
                                                .split(/\n+/g)
                                                .map(str => str.trim())
                                                .filter(str => str)
                                                .map(str => `<p>${str}</p>`)
                                                .join('')}
                                        />)}
                                <div
                                    className="card card-border pad-2-t pad-2-b body-4"
                                    style={{
                                        borderTop: '3px solid #28FBAC',
                                        borderRadius: 0,
                                        color: getCssVar('--navy')
                                    }}
                                >
                                    {Boolean((data.aboutYourVenture.employees
                                            || cofounders))
                                        && (
                                            <>
                                                <div className="row-condensed flex-justify-content-space-between">
                                                    {Boolean(data.aboutYourVenture.employees)
                                                        && (
                                                            <div className="col">
                                                                <span className="display-flex flex-align-items-center">
                                                                    <svg
                                                                        className="icon-person"
                                                                        width="16"
                                                                        height="16"
                                                                        style={{
                                                                            marginRight: 5
                                                                        }}
                                                                        aria-hidden="true"
                                                                    >
                                                                        <use xlinkHref="#icon-person" />
                                                                    </svg>
                                                                    <span className="font-weight-medium">
                                                                        {data.aboutYourVenture.employees} employees
                                                                    </span>
                                                                </span>
                                                            </div>)}
                                                    {cofounders
                                                        && (
                                                            <div className="col-12 col-6-sm-up col-4-lg-up margin-t-sm-down">
                                                                <div className="row-condensed">
                                                                    <span className="col-auto font-weight-medium pad-0-r">
                                                                        Co-founder(s)
                                                                    </span>
                                                                    <span
                                                                        className="col col-12-sm-down"
                                                                        style={{
                                                                            color: getCssVar('--slate')
                                                                        }}
                                                                    >
                                                                        {cofounders}
                                                                    </span>
                                                                </div>
                                                            </div>)}
                                                </div>
                                                <hr
                                                    style={{
                                                        borderColor: getCssVar('--geyser'),
                                                        margin: '30px 0'
                                                    }}
                                                />
                                            </>)}
                                    <div className="row-condensed">
                                        <div className="col">
                                            <h3 className="body-4 font-weight-medium margin-1-b">
                                                Relevant links
                                            </h3>
                                            <RichText
                                                className="body-4"
                                                html={(data.aboutYourVenture.ventureLinks || `${
                                                    application !== null
                                                        ? 'N/A'
                                                        : '&nbsp;'}`)
                                                    .split(/\n+/g)
                                                    .map(str => str.trim())
                                                    .filter(str => str)
                                                    .map((str) => {
                                                        const formatted = str.split(/\s+/)
                                                            .map(_str => {
                                                                const urlRegex = /^(https?:\/\/)?(www\.)?([a-z0-9-]+)\.([a-z]{2,})(\/\S*)?$/i

                                                                const isUrl = urlRegex.test(_str)

                                                                if (isUrl
                                                                        && !_str.match(/^(https?:\/\/)/))
                                                                    _str = `https://${_str}`

                                                                return isUrl
                                                                    ? `<a href="${_str}">${_str}</a>`
                                                                    : _str
                                                            })
                                                            .join(' ')

                                                        return `<p>${formatted}</p>`
                                                    })
                                                    .join('')}
                                            />
                                        </div>

                                        <div className="col col-12-sm-down margin-t-sm-down">
                                            <h3 className="body-4 font-weight-medium margin-1-b">
                                                Materials for review
                                            </h3>

                                            {!data.materials.slideDeck
                                                && !data.materials.videoPitch
                                                && !data.materials.supplementalVideo
                                                && (
                                                    <p
                                                        className="body-4"
                                                        style={{
                                                            color: getCssVar('--slate'),
                                                            whiteSpace: 'pre'
                                                        }}
                                                    >
                                                        {application !== null
                                                            ? 'N/A'
                                                            : ' '}
                                                    </p>)}
                                            {Boolean(data.materials.slideDeck
                                                    || data.materials.videoPitch
                                                    || data.materials.supplementalVideo)
                                                && (
                                                    <ul>
                                                        {data.materials.slideDeck
                                                            && (
                                                                <li>
                                                                    <a
                                                                        className="flex-align-items-center"
                                                                        href={data.materials.slideDeck}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Opens in a new tab"
                                                                    >
                                                                        <svg
                                                                            className="icon-picutre-as-pdf"
                                                                            width="14"
                                                                            height="14"
                                                                            style={{
                                                                                margin: '0 5px auto 0',
                                                                                top: 2
                                                                            }}
                                                                            aria-hidden="true"
                                                                        >
                                                                            <use xlinkHref="#icon-picture-as-pdf" />
                                                                        </svg>
                                                                        <span>Slide deck</span>
                                                                    </a>
                                                                </li>)}
                                                        {data.materials.videoPitch
                                                            && (
                                                                <li>
                                                                    <a
                                                                        className="flex-align-items-center"
                                                                        href={data.materials.videoPitch}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Opens in a new tab"
                                                                    >
                                                                        <svg
                                                                            className="icon-play-circle"
                                                                            width="14"
                                                                            height="14"
                                                                            style={{
                                                                                margin: '0 5px auto 0',
                                                                                top: 2
                                                                            }}
                                                                            aria-hidden="true"
                                                                        >
                                                                            <use xlinkHref="#icon-play-circle" />
                                                                        </svg>
                                                                        <span>Video pitch</span>
                                                                    </a>
                                                                </li>)}
                                                        {data.materials.supplementalVideo
                                                            && (
                                                                <li>
                                                                    <a
                                                                        className="flex-align-items-center"
                                                                        href={data.materials.supplementalVideo}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Opens in a new tab"
                                                                    >
                                                                        <svg
                                                                            className="icon-play-circle"
                                                                            width="14"
                                                                            height="14"
                                                                            style={{
                                                                                margin: '0 5px auto 0',
                                                                                top: 2
                                                                            }}
                                                                            aria-hidden="true"
                                                                        >
                                                                            <use xlinkHref="#icon-play-circle" />
                                                                        </svg>
                                                                        <span>Supplemental video</span>
                                                                    </a>
                                                                </li>)}
                                                    </ul>)}
                                        </div>
                                    </div>
                                    <hr
                                        style={{
                                            borderColor: getCssVar('--geyser'),
                                            margin: '30px 0'
                                        }}
                                    />
                                    <ul>
                                        <li className="row-condensed margin-b margin-1-b-sm-up">
                                            <span
                                                className="col-12 col-4-sm-up"
                                            >
                                                Outside Investment in the last 12 months
                                            </span>
                                            <span
                                                className="col"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                {data.aboutYourVenture.outsideInvestment
                                                    || `${application !== null
                                                        ? 'N/A'
                                                        : ''}`}
                                            </span>
                                        </li>
                                        <li className="row-condensed margin-b margin-1-b-sm-up">
                                            <span
                                                className="col-12 col-4-sm-up"
                                            >
                                                Grants
                                            </span>
                                            <span
                                                className="col"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                {data.aboutYourVenture.grants
                                                    || `${application !== null
                                                        ? 'N/A'
                                                        : ''}`}
                                            </span>
                                        </li>
                                        <li className="row-condensed margin-b margin-1-b-sm-up">
                                            <span
                                                className="col-12 col-4-sm-up"
                                            >
                                                Revenue in the last 12 months
                                            </span>
                                            <span
                                                className="col"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                {data.aboutYourVenture.revenue
                                                    || `${application !== null
                                                        ? 'N/A'
                                                        : ''}`}
                                            </span>
                                        </li>
                                        <li className="row-condensed margin-b margin-1-b-sm-up">
                                            <span
                                                className="col-12 col-4-sm-up"
                                            >
                                                For-profit, not-for-profit, or still to be decided
                                            </span>
                                            <span
                                                className="col"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                {data.aboutYourVenture.organizationType
                                                    || `${application !== null
                                                        ? 'N/A'
                                                        : ''}`}
                                            </span>
                                        </li>
                                        <li className="row-condensed margin-b margin-1-b-sm-up">
                                            <span
                                                className="col-12 col-4-sm-up"
                                            >
                                                Product or service supported by or based on academic or scientific research
                                            </span>
                                            <span
                                                className="col"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                {data.aboutYourVenture.educationalVenture
                                                    || `${application !== null
                                                        ? 'N/A'
                                                        : ''}`}
                                            </span>
                                        </li>
                                        <li className="row-condensed margin-b margin-1-b-sm-up">
                                            <span
                                                className="col-12 col-4-sm-up"
                                            >
                                                How they harness research to improve the quality of their product or service and links to the research
                                            </span>
                                            <span
                                                className="col"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                <RichText
                                                    className="body-4"
                                                    html={(data.aboutYourVenture.research
                                                        || `${application !== null
                                                            ? 'N/A'
                                                            : ''}`)
                                                        .split(/\n+/g)
                                                        .map(str => str.trim())
                                                        .filter(str => str)
                                                        .map(str => `<p>${str}</p>`)
                                                        .join('')}
                                                />
                                            </span>
                                        </li>
                                        <li className="row-condensed">
                                            <span
                                                className="col-12 col-4-sm-up"
                                            >
                                                Competition area of focus for submission
                                            </span>
                                            <span
                                                className="col"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                {(data.aboutYourVenture.areasOfFocus
                                                        || [application !== null
                                                            ? 'N/A'
                                                            : null])
                                                    .filter(str => str)
                                                    .map(str => data.aboutYourVenture[camelCase(`areasOfFocus ${str}`)]
                                                        ? `${str}: ${data.aboutYourVenture[camelCase(`areasOfFocus ${str}`)]}`
                                                        : str)
                                                    .join(', ')}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {Boolean(Object.keys(assignment || {}).length)
                                && !isRecused
                                && ebpcAssignmentsOpen
                                && (
                                    <DashboardEbpcApplicationReviewRate
                                        assignment={assignment}
                                        assignments={assignments}
                                        steps={props.steps}
                                        onUpdate={onUpdateAssignment}
                                    />)}

                            {Boolean((reviewSteps || []).length)
                                && (
                                    <section className="dashboard-ebpc-application-review-summaries">
                                        <h2 className="heading-5 margin-b">Additional details</h2>
                                        <RichText
                                            className="body-3 margin-3-b"
                                            style={{
                                                color: getCssVar('--oxford')
                                            }}
                                        >
                                            <p>
                                                Each venture responded to a series of prompts across
                                                a variety of topics. Below you’ll find each category, the
                                                prompt, followed by the venture’s response.
                                            </p>
                                        </RichText>
                                        {reviewSteps
                                            .map((obj, idx) => (
                                                <div
                                                    key={`dashboard-ebpc-application-review-summaries-item-${idx}`}
                                                    className={`dashboard-ebpc-application-review-summaries-item${
                                                        reviewSteps.length > idx + 1
                                                            ? ' margin-3-b'
                                                            : ''}${
                                                        idx
                                                            ? ' pad-3-t'
                                                            : ''}`}
                                                    style={{
                                                        borderTop: idx
                                                            ? `1px solid ${getCssVar('--geyser')}`
                                                            : null
                                                    }}
                                                >
                                                    <h3 className="body-3 font-weight-medium">{obj.title}</h3>
                                                    <RichText
                                                        className="body-4"
                                                        html={obj.richText}
                                                        style={{
                                                            color: getCssVar('--navy'),
                                                            marginTop: 15
                                                        }}
                                                    />
                                                    <p
                                                        className="body-4 margin-1-t"
                                                    >
                                                        {obj.answer || 'N/A'}
                                                    </p>
                                                    {obj.slug === 'finances'
                                                        && (data.finances || {}).financesFile
                                                        && (
                                                            <p className="body-5 margin-1-t">
                                                                <a
                                                                    className="display-inline-flex flex-align-items-center"
                                                                    href={data.finances.financesFile}
                                                                    target="_blank"
                                                                    title="Opens in a new tab"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        color: getCssVar('--navy'),
                                                                        textDecoration: 'underline'
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            marginRight: 5,
                                                                            lineHeight: 1
                                                                        }}
                                                                    >
                                                                        Review material
                                                                    </span>
                                                                    <svg width="16" height="16">
                                                                        <use xlinkHref="#icon-launch" />
                                                                    </svg>
                                                                </a>
                                                            </p>
                                                        )}
                                                </div>
                                            ))}
                                    </section>)}
                            {nextFinalsLink
                                && (
                                    <nav
                                        className="row-condensed flex-justify-content-space-between margin-3-t"
                                        aria-label="Venture applications"
                                    >
                                        <div className="col-auto margin-auto margin-0-r">
                                            <Link
                                                to={nextFinalsLink}
                                                className="btn btn-lg"
                                            >
                                                Next venture
                                            </Link>
                                        </div>
                                    </nav>)}
                        </div>
                    </div>
                    {isEbpcJudge
                        && ebpcAssignmentsOpen
                        && (
                            <aside
                                id="dashboard-ebpc-application-review-rankings"
                                className="col-12 col-4-xl-up margin-1-t-lg-down margin-3-t-lg"
                            >
                                <div className="card card-offscreen-x-lg-down">
                                    <h2 className="heading-5 margin-1-b">Rank ventures</h2>
                                    <RichText
                                        className="body-4"
                                        style={{
                                            color: getCssVar('--oxford')
                                        }}
                                    >
                                        <p>
                                            Ventures are automatically ranked based on the
                                            overall rating assigned during your review.
                                        </p>
                                        <p>
                                            Drag and drop a venture to customize your rankings.
                                        </p>
                                        <p>
                                            <em>
                                                Review the&nbsp;
                                                <a
                                                    href="/dashboard/ebpc/judge-orientation"
                                                    style={{
                                                        color: 'inherit'
                                                    }}
                                                >
                                                    Judge orientation page
                                                </a>
                                                &nbsp;for additional help with judging.
                                            </em>
                                        </p>
                                    </RichText>
                                    <div className="dashboard-ebpc-applications pad-3-t">
                                        <DashboardEbpcAssignmentsTable
                                            refreshAssignments={refreshAssignments}
                                            rankingStatusBadge
                                            draggable
                                        />
                                    </div>
                                    <p
                                        className="row-condensed body-5 margin-t"
                                        role="status"
                                        style={{
                                            fontWeight: 300
                                        }}
                                    >
                                        <span className="col-auto pad-1-r">
                                            <svg width="16" height="16" aria-hidden="true">
                                                <use xlinkHref="#icon-info" />
                                            </svg>
                                        </span>
                                        <span className="col pad-0-l">
                                            Ranked by overall rating. 1 is the highest rank
                                        </span>
                                    </p>
                                </div>
                            </aside>)}
                    {isEbpcReviewer
                        && ebpcReviewsOpen
                        && Boolean(Object.keys(
                            ((application || {}).ebpc_review || {})).length)
                        && authProvider.getIdentity().id
                            === application.ebpc_review.ebpc_reviewer_id
                        && application.ebpc_review
                            .ebpc_application
                            .ebpc_review_approved
                                === application.ebpc_review.approved
                        && (
                            <aside
                                id="dashboard-ebpc-application-review"
                                className="col-12 col-4-xl-up margin-1-t-lg-down margin-3-t-lg"
                            >
                                <div className="card card-offscreen-x-lg-down">
                                    <h2 className="heading-5 margin-1-b">Review venture</h2>
                                    <DashboardEbpcApplicationReviewerForm
                                        {...application.ebpc_review}
                                    />
                                </div>
                            </aside>)}
                </div>
            </div>
        </div>
    )
}
